/**
 * LOGIN-LINK — carrying a link-borne session from the login origin into a persona SPA, and restoring
 * it on reload. Storage and its rationale: `linkSessionStorage.ts`.
 *
 * ─── why it cannot ride the ordinary handoff ────────────────────────────────────────────────────────
 * `authHandoff` carries a token PAIR and is skipped on a same-site hop, because there the refresh
 * cookie restores the session. A link session has no refresh token and no cookie (redeeming the link
 * deliberately CLEARS the cookie, so the previous user cannot come back on the next 401). So this
 * handoff ALWAYS carries the access token, same site or not — there is no other way for it to arrive.
 *
 * Same fragment discipline as `authHandoff`: a fragment never reaches a server or a `Referer`, it is
 * stripped with `replaceState` before anything else, and it is only written to or read from a secure
 * context. Distinct keys (`zc_lat`/`zc_lexp`) so the two handoffs can never be confused — a link token
 * parsed as a pair would be sent to /auth/refresh as if it were a refresh token.
 */
import { isSecureContextHost, type HandoffLocation } from './authHandoff';
import { readLinkSession, storeLinkSession, type LinkSession } from './linkSessionStorage';
import { useSessionStore } from './sessionStore';

export type { LinkSession } from './linkSessionStorage';
export { readLinkSession, clearLinkSession } from './linkSessionStorage';

const ACCESS_KEY = 'zc_lat';
const EXPIRES_KEY = 'zc_lexp';

/** `#zc_lat=…&zc_lexp=…`. Exported for tests; callers use `assignWithLinkSession`. */
export function buildLinkSessionFragment(session: LinkSession): string {
  const params = new URLSearchParams();
  params.set(ACCESS_KEY, session.accessToken);
  params.set(EXPIRES_KEY, session.expiresAt);
  return `#${params.toString()}`;
}

/** The inverse. Null unless both halves are present. */
export function parseLinkSessionFragment(hash: string): LinkSession | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const accessToken = params.get(ACCESS_KEY);
  const expiresAt = params.get(EXPIRES_KEY);
  return accessToken && expiresAt ? { accessToken, expiresAt } : null;
}

/**
 * Login origin → persona SPA, carrying the link session. `url` must already come from the app registry
 * (`resolveAuthOriginRoute` / `appUrl`), exactly as for `assignWithHandoff`. Over plain http on a
 * non-local host the token is NOT attached — the user then lands as a guest, which is the safe failure.
 */
export function assignWithLinkSession(url: string, session: LinkSession, loc: HandoffLocation = window.location): void {
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    window.location.assign(url);
    return;
  }
  const carry = isSecureContextHost(loc.protocol, loc.hostname) && isSecureContextHost(target.protocol, target.hostname);
  window.location.assign(carry ? `${target.toString()}${buildLinkSessionFragment(session)}` : target.toString());
}

/**
 * Persona SPA bootstrap, step 1: take a link session out of the URL (stripping the fragment first),
 * keep it for reloads, and seed the store with the access token. Returns true when one was consumed.
 */
export function consumeLinkSessionHandoff(loc: HandoffLocation = window.location, nowMs: number = Date.now()): boolean {
  if (!loc.hash) return false;
  const session = parseLinkSessionFragment(loc.hash);
  if (!session) return false;
  if (!isSecureContextHost(loc.protocol, loc.hostname)) return false;

  try {
    globalThis.history?.replaceState(null, '', `${loc.pathname}${loc.search}`);
  } catch {
    /* no history API — still better than failing the sign-in */
  }

  if (!(Date.parse(session.expiresAt) > nowMs)) return false; // arrived dead (a stale bookmark of the hop)

  // ⚠ clearSession FIRST: whatever this tab held before belongs to someone else now, and setTokens
  // keeps the previous refresh token when it is given none.
  useSessionStore.getState().clearSession();
  storeLinkSession(session);
  useSessionStore.getState().setTokens(session.accessToken);
  return true;
}

/** Persona SPA bootstrap, step 2 (a reload): re-seed the store from this tab's stored link session. */
export function restoreLinkSession(nowMs: number = Date.now()): boolean {
  const session = readLinkSession(nowMs);
  if (!session) return false;
  useSessionStore.getState().setTokens(session.accessToken);
  return true;
}

/** Is this tab running on a link-borne session? Drives the "signed in via link" notice and refresh. */
export function isLinkSession(nowMs: number = Date.now()): boolean {
  return readLinkSession(nowMs) !== null;
}

